import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/authContext";

function LoginSection() {
  const { t } = useTranslation();
  const { signIn, loginError } = useAuth();

  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await signIn(username, password);
    } catch {
      // Error is surfaced via loginError from auth context
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      p={8}
      bg="rgba(13, 13, 21, 0.8)"
      borderRadius="xl"
      boxShadow="0 8px 32px rgba(0, 0, 0, 0.4)"
      border="1px solid"
      borderColor="whiteAlpha.100"
      backdropFilter="blur(12px)"
      w="100%"
    >
      <form onSubmit={handleSubmit}>
        <VStack spacing={5}>
          <FormControl>
            <FormLabel color="primary.200" fontSize="sm" fontWeight="500" letterSpacing="0.05em">
              {t("Username")}
            </FormLabel>
            <Input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t("Username")}
              bg="whiteAlpha.50"
              color="primary.100"
              borderColor="whiteAlpha.200"
              borderRadius="md"
              size="lg"
              _placeholder={{ color: "primary.400" }}
              _hover={{ borderColor: "whiteAlpha.300" }}
              _focus={{
                borderColor: "cyan.400",
                boxShadow: "0 0 0 1px rgba(0, 255, 255, 0.3)",
              }}
              autoComplete="username"
            />
          </FormControl>

          <FormControl>
            <FormLabel color="primary.200" fontSize="sm" fontWeight="500" letterSpacing="0.05em">
              {t("Password")}
            </FormLabel>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("Password")}
              bg="whiteAlpha.50"
              color="primary.100"
              borderColor="whiteAlpha.200"
              borderRadius="md"
              size="lg"
              _placeholder={{ color: "primary.400" }}
              _hover={{ borderColor: "whiteAlpha.300" }}
              _focus={{
                borderColor: "cyan.400",
                boxShadow: "0 0 0 1px rgba(0, 255, 255, 0.3)",
              }}
              autoComplete="current-password"
            />
          </FormControl>

          {loginError !== null && (
            <Text color="red.400" fontSize="sm" w="100%">
              {loginError}
            </Text>
          )}

          <Button
            type="submit"
            size="lg"
            width="100%"
            mt={2}
            bg="transparent"
            color="cyan.300"
            border="1px solid"
            borderColor="cyan.400"
            borderRadius="md"
            fontWeight="600"
            letterSpacing="0.05em"
            isDisabled={isLoading || username.length === 0 || password.length === 0}
            isLoading={isLoading}
            spinner={<Spinner size="sm" />}
            _hover={{
              bg: "rgba(0, 255, 255, 0.1)",
              boxShadow: "0 0 20px rgba(0, 255, 255, 0.2)",
            }}
            _active={{
              bg: "rgba(0, 255, 255, 0.15)",
            }}
          >
            {t("Sign In")}
          </Button>
        </VStack>
      </form>
    </Box>
  );
}

export default LoginSection;
